import type { ActionAppFacilities } from "@glide/common-core/dist/js/components/types";
import type { BaseRowIndex, LoadingValue, Row } from "@glide/computation-model-types";
import {
    isBaseRowIndex,
    isLiveUpdateQuery,
    isLoadingValue,
    MutableTable,
    unwrapLoadingValue,
    type SerializedQuery,
} from "@glide/computation-model-types";
import {
    nativeTableRowDeletedColumnName,
    nativeTableRowIDColumnName,
    rowIndexColumnName,
    type TableGlideType,
} from "@glide/type-schema";
import { isTable } from "@glide/common-core/dist/js/computation-model/data";
import { getFeatureSetting } from "@glide/common-core";
import { convertSerializableValueToCellValue, type ColumnCellValues } from "@glide/data-types";
import type { JSONObject } from "@glide/support";
import { hasOwnProperty } from "@glideapps/ts-necessities";

export function getRowIndex(rowData: JSONObject): BaseRowIndex | undefined {
    const maybeRowIndex = rowData[rowIndexColumnName];
    if (isBaseRowIndex(maybeRowIndex)) {
        return maybeRowIndex;
    } else {
        return undefined;
    }
}

function getRowDeleted(rowData: JSONObject): boolean | undefined {
    const maybeRowDeleted = rowData[nativeTableRowDeletedColumnName];
    if (typeof maybeRowDeleted === "boolean") return maybeRowDeleted;
    return undefined;
}

// exported for testing
export function makeRowIDForRow(
    table: TableGlideType | undefined,
    row: object,
    appFacilities: ActionAppFacilities
): string {
    const rowIDColumn = table?.rowIDColumn;
    if (rowIDColumn !== undefined && hasOwnProperty(row, rowIDColumn)) {
        const v = row[rowIDColumn];
        if (typeof v === "string") {
            return v;
        } else if (typeof v === "number") {
            return v.toString();
        }
    }
    return appFacilities.makeRowID();
}

// exported for testing
export function makeRowFromRowData(
    table: TableGlideType | undefined,
    rowID: string,
    rowData: JSONObject,
    rowIndexFallback: BaseRowIndex
): Row {
    const rowIndex = getRowIndex(rowData) ?? rowIndexFallback;

    const cells: ColumnCellValues = {};
    // Most internals of Glide are only prepared to handle `undefined`, not `null`.
    // But SQL data sources only have `null`, not `undefined`. Instead of imposing
    // a JavaScript conversion requirement on these sources, we'll fix this
    // discrepancy here.
    if (table !== undefined) {
        for (const c of table.columns) {
            const value = rowData[c.name] ?? undefined;
            const cellValue = convertSerializableValueToCellValue(
                {
                    flattenArrays: false,
                    semiStrictConvertSerializableValue: getFeatureSetting("semiStrictConvertSerializableValue"),
                },
                value
            );
            if (cellValue === undefined) continue;
            cells[c.name] = cellValue;
        }
    } else {
        for (const [k, v] of Object.entries(rowData)) {
            const value = v ?? undefined;
            const cellValue = convertSerializableValueToCellValue(
                {
                    flattenArrays: false,
                    semiStrictConvertSerializableValue: getFeatureSetting("semiStrictConvertSerializableValue"),
                },
                value
            );
            if (cellValue === undefined) continue;
            cells[k] = cellValue;
        }
    }

    return {
        ...cells,
        [nativeTableRowIDColumnName]: rowID,
        $isVisible: true,
        [rowIndexColumnName]: rowIndex,
        [nativeTableRowDeletedColumnName]: getRowDeleted(rowData),
    };
}

// exported only for testing
export function decomposeRowIDQuery(query: SerializedQuery, table: TableGlideType | undefined): string | undefined {
    // This shouldn't happen, but just in case.
    if (table?.rowIDColumn === undefined) return undefined;

    if (query.groupBy !== undefined) return undefined;
    if (query.limit !== undefined && query.limit < 1) return undefined;
    if (query.search !== undefined) return undefined;
    if (query.conditions.length !== 1) return undefined;
    if (query.conditions[0].length !== 1) return undefined;
    const condition = query.conditions[0][0];

    if (condition.negated) return undefined;

    if (condition.kind === "equals") {
        if (condition.lhs.columnName !== table.rowIDColumn) return undefined;
        if (typeof condition.rhs !== "string") return undefined;
        return condition.rhs;
    } else if (condition.kind === "array-overlap") {
        if (condition.column.columnName !== table.rowIDColumn) return undefined;
        if (condition.array.length !== 1) return undefined;
        if (typeof condition.array[0] !== "string") return undefined;
        return condition.array[0];
    }

    return undefined;
}

export function adjustRequeryLimit(query: SerializedQuery, maxLimit: number): SerializedQuery {
    if (!isLiveUpdateQuery(query)) return query;
    return { ...query, limit: maxLimit };
}

export function unwrapLoadingTable(active: MutableTable | LoadingValue): MutableTable {
    if (!isLoadingValue(active)) return active;

    const unwrapped = unwrapLoadingValue(active);
    if (isLoadingValue(unwrapped) || !isTable(unwrapped)) return new MutableTable();
    return new MutableTable(unwrapped.asArray());
}
